import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  FiHome, FiUsers, FiMessageSquare, FiCalendar,
  FiBarChart2, FiSettings, FiLogOut, FiMenu, FiX, FiAward, FiSmartphone, FiBell, FiVideo, FiLifeBuoy, FiEye, FiLogIn, FiTrash2, FiActivity, FiGlobe, FiUserPlus, FiBriefcase, FiDollarSign, FiPieChart, FiTrendingUp, FiKey, FiShoppingBag, FiGrid, FiShield, FiFileText, FiHeart, FiMonitor, FiMap, FiLayers, FiStar, FiBookOpen, FiMapPin, FiCheckSquare, FiFlag, FiAlertOctagon
} from 'react-icons/fi';
import AnimatedBackground from './AnimatedBackground';
import RulesBanner from './RulesBanner';
import blueLogo from '../assets/bluelogo.png';
import { notificationsAPI } from '../services/api';
import useActivityHeartbeat from '../hooks/useActivityHeartbeat';
import './Layout.css';

// Side menu groups (brainstorms/2026-10-29-stand-in-access-and-several-cmos.md Q7): related pages sit
// together under a small label, each group on its own soft background. Which items a person sees is
// still decided per item below; a group with nothing visible is left out.
const MENU_GROUPS = [
  { key: 'day', label: 'My day', paths: ['/dashboard', '/action-board', '/my-duties', '/my-analytics'] },
  { key: 'clients', label: 'Clients', paths: ['/students', '/medical-tourism', '/job-clients', '/tourist-clients', '/alumni', '/deleted-students'] },
  { key: 'cases', label: 'Case work', paths: ['/visa-cases', '/settling', '/job-operations', '/service-orders', '/country-guide', '/app-logins'] },
  { key: 'leads', label: 'Leads & sales', paths: ['/app-leads', '/website-leads', '/deals'] },
  { key: 'marketing', label: 'Marketing', paths: ['/marketing', '/marketing-config', '/app-funnel', '/student-app-admin'] },
  { key: 'partners', label: 'Partners', paths: ['/agents', '/agencies', '/sub-agent-applications', '/partner-analytics', '/partnerships', '/commissions', '/growth-access'] },
  { key: 'money', label: 'Money', paths: ['/billing', '/payments-desk', '/finance'] },
  { key: 'care', label: 'Client care', paths: ['/support-inbox', '/feedback', '/complaints'] },
  { key: 'lessons', label: 'Lessons', paths: ['/language-lessons', '/learners'] },
  { key: 'team', label: 'Team', paths: ['/meetings', '/team-tasks', '/team-rules'] },
  { key: 'admin', label: 'Admin & privacy', paths: ['/data-requests', '/audit-log', '/user-management', '/settings'] },
];
// The group a covered role works from, moved up the menu with a "Covering" badge while the cover runs.
const COVER_GROUP = { CMO: 'marketing', CSM: 'leads' };

const groupMenu = (items, { cmo, coveredRoles }) => {
  const byPath = Object.fromEntries(items.map((i) => [i.path, i]));
  const covering = new Set(coveredRoles.map((r) => COVER_GROUP[r]).filter(Boolean));
  // The CMO's own screens come first for him (founder, 22 Oct 2026): the plan, then the lead inboxes.
  const early = cmo ? ['marketing', 'leads'] : [];
  const rank = (g) => (g.key === 'day' ? 0 : covering.has(g.key) ? 1 : early.includes(g.key) ? 2 : 3);
  const groups = MENU_GROUPS
    .map((g) => ({ ...g, covering: covering.has(g.key), items: g.paths.map((p) => byPath[p]).filter(Boolean) }))
    .filter((g) => g.items.length > 0);
  const grouped = new Set(MENU_GROUPS.flatMap((g) => g.paths));
  const rest = items.filter((i) => !grouped.has(i.path));
  if (rest.length) groups.push({ key: 'other', label: 'More', items: rest });
  return groups.map((g, idx) => ({ ...g, idx })).sort((a, b) => rank(a) - rank(b) || a.idx - b.idx);
};

const Layout = ({ children }) => {
  const {
    user, logout, isAdmin, isEmployee, isAnyStaff, isInternalStaff, isCoreTeam, isCmo, canViewMarketing, hasDuties,
    isFinanceLeadership,
    isSalesLeadership, isMarketingLeadership, showCeoStrategicMetrics, hasFinanceAccess,
    hasMobileAdminAccess, isChairman, hasGrowthStackAccess, canRecordPayments, isAuthenticated,
    isCoach, canOpenMyAnalytics, canOpenHome, covers, isCovering,
  } = useAuth();
  // "Cost of time" input — see VEC_MS_FINANCE_SEEDING_PLAN.md Phase 4. Layout
  // is mounted once for the whole authenticated app shell, so this doesn't
  // reset or duplicate on every page navigation.
  useActivityHeartbeat(isAuthenticated);
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const notificationsRef = useRef(null);

  const fetchUnreadCount = () => {
    notificationsAPI.unreadCount()
      .then((res) => setUnreadCount(res.data?.count ?? 0))
      .catch(() => {});
  };

  const fetchNotifications = () => {
    setNotificationsLoading(true);
    notificationsAPI.list({ limit: 30 })
      .then((res) => setNotifications(res.data?.results ?? res.data ?? []))
      .catch(() => setNotifications([]))
      .finally(() => setNotificationsLoading(false));
  };

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (notificationsOpen) {
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [notificationsOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notificationsRef.current && !notificationsRef.current.contains(e.target)) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkRead = (n) => {
    if (n.read) return;
    notificationsAPI.markRead(n.id).then(() => {
      setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnreadCount((c) => Math.max(0, c - 1));
    }).catch(() => {});
  };

  const handleMarkAllRead = () => {
    notificationsAPI.markAllRead().then(() => {
      setNotifications((prev) => prev.map((x) => ({ ...x, read: true })));
      setUnreadCount(0);
    }).catch(() => {});
  };

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    // Initialize based on window width
    if (typeof window !== 'undefined') {
      return window.innerWidth > 768;
    }
    return true;
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    // Home: the nine internal team roles (students/home.py); agents and the Coach don't get it.
    { path: '/dashboard', icon: FiHome, label: 'Home', homeOnly: true },
    // hideForCmo: the CMO sees no client records (lists would be empty for him anyway);
    // his menu leads with the marketing plan, leads and the Action Board (founder, 22 Oct 2026).
    { path: '/students', icon: FiUsers, label: 'Students', adminOnly: false, hideForCmo: true },
    // Second client vertical alongside Students — same staff-managed visibility
    // (open to every authenticated staff role; the backend itself scopes agents
    // down to only their own assigned clients).
    { path: '/medical-tourism', icon: FiHeart, label: 'Medical Tourism', adminOnly: false, hideForCmo: true },
    // Job-seeker (work-abroad) and normal-tourism verticals — same visibility as
    // Students/Medical Tourism. Deliberately separate lists/IDs so a Job or
    // Tourist conversion never lands on the Students list.
    { path: '/job-clients', icon: FiBriefcase, label: 'Job Seekers', adminOnly: false, hideForCmo: true },
    // Employers, vacancies, destination config, aftercare — internal staff only.
    { path: '/job-operations', icon: FiShield, label: 'Job Operations', internalStaffOnly: true },
    { path: '/tourist-clients', icon: FiMap, label: 'Tourists', adminOnly: false, hideForCmo: true },
    // People who only take lessons (not students, job seekers, tourists or medical
    // clients). Lessons staff only, like Language Lessons; the API enforces it.
    { path: '/learners', icon: FiBookOpen, label: 'Learners', internalStaffOnly: true, hideForCmo: true },
    // Country Education Reference (document tracker follow-up, 2026-09-29) —
    // sourced from Türkiye's MEB Denklik Kılavuzu. Non-sensitive official
    // guide content, so open to agents too (same visibility as Students).
    { path: '/country-guide', icon: FiLayers, label: 'Country Guide', internalStaffOnly: true },
    // Turkish lessons: classes, schedule, learners, packages, teachers. The API
    // limits it to leadership, admins and consultants (CFO: packages only).
    { path: '/language-lessons', icon: FiBookOpen, label: 'Language Lessons', internalStaffOnly: true },
    // The CMO's own screens first for him: the plan, then the two lead inboxes.
    { path: '/marketing', icon: FiCalendar, label: 'Marketing Plan', marketingViewOnly: true },
    { path: '/app-leads', icon: FiSmartphone, label: 'App Leads', coreTeamOnly: true, cmoOnly: true },
    { path: '/website-leads', icon: FiMonitor, label: 'Website Leads', coreTeamOnly: true, cmoOnly: true },
    { path: '/action-board', icon: FiGrid, label: 'Action Board', adminOrEmployeeOnly: true, coreTeamOnly: true },
    { path: '/deals', icon: FiTrendingUp, label: 'Deals', adminOnly: false },
    // Per-person KPIs and RACI areas (brainstorms/2026-10-23-per-role-analytics.md).
    { path: '/my-analytics', icon: FiBarChart2, label: 'My Analytics', myAnalyticsOnly: true },
    { path: '/my-duties', icon: FiCheckSquare, label: 'My Duties', dutiesOnly: true },
    // Team meetings + tasks (Foundation framework): internal team only, never agents.
    { path: '/meetings', icon: FiVideo, label: 'Meetings', coreTeamOnly: true },
    { path: '/team-tasks', icon: FiCheckSquare, label: 'Team Tasks', coreTeamOnly: true },
    // Foundation: team rules (everyone signs), roles & stand-ins, contracts. Open to every CRM login.
    { path: '/team-rules', icon: FiFlag, label: 'Team & Rules', adminOnly: false },
    // Client complaints (Foundation Q12): any internal team member logs one.
    { path: '/complaints', icon: FiAlertOctagon, label: 'Complaints', internalStaffOnly: true },
    { path: '/agents', icon: FiUsers, label: 'Agents & Employees', adminOnly: true, salesOnly: true },
    // adminOrEmployeeOnly = isAnyStaff(), which already includes CSM (see is_any_staff) — no
    // separate salesOnly flag needed here, unlike /agents and /commissions below (plain isAdmin()).
    { path: '/alumni', icon: FiAward, label: 'Alumni', internalStaffOnly: true },
    // mobileAdminOnly = hasMobileAdminAccess() (CEO/COO/Chairman/CMO by role, or a plain
    // ADMIN/employee holding an active delegation) — matches ProtectedRoute's route guard
    // exactly. Deliberately NOT adminOrEmployeeOnly: that would leak the nav link to every
    // staff role (including the new CSM, via is_any_staff) even though the route itself
    // redirects them straight back out, which is a dead/misleading link, not real access.
    { path: '/student-app-admin', icon: FiSmartphone, label: 'Student App Admin', mobileAdminOnly: true },
    { path: '/support-inbox', icon: FiLifeBuoy, label: 'Support Inbox', internalStaffOnly: true },
    { path: '/app-leads', icon: FiSmartphone, label: 'App Leads', coreTeamOnly: true, notForCmo: true },
    // App Leads = mobile-app-sourced only; Website Leads mirrors it exactly
    // (same triage UI, same intent-aware convert buttons) but for the
    // website's Job/Tourism/Education consultation submissions — kept in a
    // separate inbox so the two origins are never mixed.
    { path: '/website-leads', icon: FiMonitor, label: 'Website Leads', coreTeamOnly: true, notForCmo: true },
    { path: '/visa-cases', icon: FiFileText, label: 'Visa Cases', internalStaffOnly: true },
    // Customer Support Officers settle clients on the ground after visa approval; the COO's board.
    { path: '/settling', icon: FiMapPin, label: 'Arrival & Settling', internalStaffOnly: true },
    { path: '/feedback', icon: FiStar, label: 'Feedback', internalStaffOnly: true },
    { path: '/app-funnel', icon: FiTrendingUp, label: 'App Funnel', internalStaffOnly: true },
    { path: '/app-logins',       icon: FiLogIn,    label: 'Application Logins', internalStaffOnly: true },
    { path: '/deleted-students', icon: FiTrash2,   label: 'Deleted Students',   internalStaffOnly: true },
    { path: '/audit-log', icon: FiActivity, label: 'Audit Log', adminOnly: true },
    { path: '/data-requests', icon: FiShield, label: 'Data Requests', internalStaffOnly: true },
    { path: '/agencies', icon: FiGlobe, label: 'Agencies & Countries', growthStackOnly: true, salesOnly: true },
    { path: '/sub-agent-applications', icon: FiUserPlus, label: 'Sub-Agent Applications', growthStackOnly: true },
    // CSM (salesOnly) reads Agencies + Partner Analytics; suspend/approve/Growth Access stay closed to them.
    { path: '/partner-analytics', icon: FiLayers, label: 'Partner Analytics', growthStackOnly: true, salesOnly: true },
    { path: '/growth-access', icon: FiUserPlus, label: 'Growth Access', adminOnly: true },
    { path: '/partnerships', icon: FiBriefcase, label: 'University Partnerships', adminOnly: true },
    { path: '/commissions', icon: FiDollarSign, label: 'Commissions', adminOnly: true, salesOnly: true },
    { path: '/service-orders', icon: FiShoppingBag, label: 'Service Orders', adminOrEmployeeOnly: true },
    // The CFO isn't in isInternalStaff but records every payment (founder rule, 19 Oct 2026),
    // so gets Billing Documents plus a Payments desk for the student / medical / job ledgers.
    { path: '/billing', icon: FiFileText, label: 'Billing Documents', internalStaffOnly: true, paymentsOnly: true },
    { path: '/payments-desk', icon: FiDollarSign, label: 'Record Payments', paymentsOnly: true },
    { path: '/finance', icon: FiPieChart, label: 'Finance', financeOnly: true },
    { path: '/marketing-config', icon: FiTrendingUp, label: 'Marketing Budget', marketingOnly: true },
    { path: '/settings', icon: FiSettings, label: 'Settings', adminOnly: false },
    // Chairman-only offboarding tool — reset any staff member's login credentials.
    // Deliberately narrower than adminOnly (see users/permissions.py IsChairmanRole).
    { path: '/user-management', icon: FiKey, label: 'User Credentials', chairmanOnly: true },
  ];

  const filteredMenuItems = menuItems.filter(item => {
    // The Coach sees no client records: only analytics and their own settings (Q7a).
    if (isCoach()) return ['/my-analytics', '/settings'].includes(item.path);
    // The lead inboxes appear twice: near the top for the CMO (cmoOnly), in their usual place for everyone else.
    if (item.hideForCmo && isCmo() && !isCovering('CSM')) return false;
    if (item.cmoOnly && !isCmo()) return false;
    if (item.notForCmo && isCmo()) return false;
    // Each applicable flag contributes one OR'd condition — an item with
    // multiple flags (e.g. /agents: adminOnly + salesOnly) shows if ANY of
    // them pass, not just the first one checked. A plain `adminOnly: false`
    // (or no flags at all) means "open to every authenticated staff member".
    const checks = [];
    if (item.adminOnly) checks.push(isAdmin());
    if (item.adminOrEmployeeOnly) checks.push(isAnyStaff());
    if (item.internalStaffOnly) checks.push(isInternalStaff());
    if (item.coreTeamOnly) checks.push(isCoreTeam());
    if (item.marketingViewOnly) checks.push(canViewMarketing());
    if (item.dutiesOnly) checks.push(hasDuties());
    if (item.myAnalyticsOnly) checks.push(canOpenMyAnalytics());
    if (item.homeOnly) checks.push(canOpenHome());
    if (item.mobileAdminOnly) checks.push(hasMobileAdminAccess());
    if (item.financeOnly) checks.push(hasFinanceAccess());
    if (item.salesOnly) checks.push(isSalesLeadership());
    if (item.marketingOnly) checks.push(isMarketingLeadership());
    if (item.ceoStrategicOnly) checks.push(showCeoStrategicMetrics());
    if (item.chairmanOnly) checks.push(isChairman());
    if (item.growthStackOnly) checks.push(hasGrowthStackAccess());
    if (item.paymentsOnly) checks.push(canRecordPayments());
    return checks.length === 0 || checks.some(Boolean);
  });

  // Handle responsive sidebar behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    
    // Set initial state based on window size
    if (typeof window !== 'undefined') {
      handleResize();
    }
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Close sidebar when navigating on mobile
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  }, [location.pathname]);

  return (
    <div className="layout">
      <AnimatedBackground />
      {sidebarOpen && typeof window !== 'undefined' && window.innerWidth <= 768 && (
        <div 
          className="sidebar-overlay" 
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo-container">
            <img 
              src={blueLogo} 
              alt="VEMS Logo" 
              className="sidebar-logo"
            />
            {sidebarOpen && <h2>VEMS</h2>}
          </div>
          <button 
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>
        
        <nav className="sidebar-nav">
          {groupMenu(filteredMenuItems, { cmo: isCmo(), coveredRoles: covers.map((c) => c.role) }).map((group) => (
            <div key={group.key} className="nav-group" data-group={group.key}>
              {sidebarOpen && (
                <div className="nav-group-label">
                  {group.label}
                  {group.covering && <span className="nav-group-badge">Covering</span>}
                </div>
              )}
              {group.items.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`nav-item ${isActive ? 'active' : ''}`}
                    title={sidebarOpen ? undefined : `${group.label} · ${item.label}`}
                  >
                    <Icon className="nav-icon" />
                    {sidebarOpen && <span className="nav-label">{item.label}</span>}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            {sidebarOpen && (
              <>
                <div className="user-name">{user?.username}</div>
                <div className="user-role">{user?.role}</div>
              </>
            )}
          </div>
          <button className="logout-button" onClick={handleLogout}>
            <FiLogOut />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      <main className="main-content">
        <header className="top-header">
          <button 
            className="mobile-menu-button"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle menu"
          >
            <FiMenu />
          </button>
          <h1>{filteredMenuItems.find(item => item.path === location.pathname)?.label || 'Home'}</h1>
          <div className="header-actions">
            <div className="notifications-wrapper" ref={notificationsRef}>
              <button
                type="button"
                className="notifications-bell"
                onClick={() => setNotificationsOpen((o) => !o)}
                aria-label="Notifications"
              >
                <FiBell />
                {unreadCount > 0 && (
                  <span className="notifications-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
              </button>
              {notificationsOpen && (
                <div className="notifications-dropdown">
                  <div className="notifications-dropdown-header">
                    <span>Notifications</span>
                    {unreadCount > 0 && (
                      <button type="button" className="notifications-mark-all" onClick={handleMarkAllRead}>
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="notifications-list">
                    {notificationsLoading ? (
                      <div className="notifications-loading">Loading…</div>
                    ) : notifications.length === 0 ? (
                      <div className="notifications-empty">No notifications</div>
                    ) : (
                      notifications.map((n) => (
                        <button
                          key={n.id}
                          type="button"
                          className={`notification-item ${n.read ? 'read' : ''}`}
                          onClick={() => handleMarkRead(n)}
                        >
                          <span className="notification-message">{n.message}</span>
                          <span className="notification-meta">
                            {n.actor_username} · {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <span className="welcome-text">Welcome, {user?.username}!</span>
          </div>
        </header>
        
        <div className="content-wrapper">
          <RulesBanner />
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;

